import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import type { GamePassGrant } from '@/lib/trpc'

const MAX_BALANCE = 2_147_483_647
const GRANT_ERRORS = new Set([
  'game_pass_recipient_not_found',
  'game_pass_recipient_ambiguous',
  'game_pass_recipient_changed',
  'game_pass_grant_request_conflict',
  'game_pass_balance_limit',
])

export function PromoCodesSection() {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [selection, setSelection] = useState<{ email: string, version: number } | null>(null)
  const [locked, setLocked] = useState(false)

  function lookup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (locked || !email.trim())
      return
    setSelection(previous => ({ email: email.trim(), version: (previous?.version ?? 0) + 1 }))
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Card>
        <CardHeader>
          <CardTitle>{t('promoCodes.title')}</CardTitle>
          <CardDescription>{t('promoCodes.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form data-testid="game-pass-recipient-form" onSubmit={lookup} className="space-y-2" aria-busy={locked}>
            <Label htmlFor="game-pass-recipient-email">{t('promoCodes.email')}</Label>
            <div className="flex items-start gap-2">
              <Input id="game-pass-recipient-email" data-testid="game-pass-recipient-email" type="email" value={email} onChange={(event) => { setEmail(event.target.value); setSelection(null) }} required disabled={locked} autoComplete="off" spellCheck={false} dir="ltr" className="min-h-11 flex-1" />
              <Button data-testid="game-pass-recipient-lookup" type="submit" variant="outline" disabled={locked || !email.trim()} className="min-h-11 shrink-0">{t('promoCodes.lookup')}</Button>
            </div>
          </form>
          {selection && <RecipientGrants key={`${selection.email}:${selection.version}`} email={selection.email} onLockedChange={setLocked} />}
          <p className="mt-5 text-xs text-muted-foreground">{t('promoCodes.scope')}</p>
        </CardContent>
      </Card>
    </div>
  )
}

function RecipientGrants({ email, onLockedChange }: { email: string, onLockedChange: (locked: boolean) => void }) {
  const trpc = useTRPC()
  const { t, language } = useI18n()
  const recipient = useQuery({
    ...trpc.promoGamePassRouter.adminResolveRecipient.queryOptions({ email }),
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  })
  const [expectedUserId, setExpectedUserId] = useState<string | null>(null)
  const [count, setCount] = useState('3')
  const [reason, setReason] = useState('')
  const [countTouched, setCountTouched] = useState(false)
  const [reasonTouched, setReasonTouched] = useState(false)
  const [requestId, setRequestId] = useState(() => crypto.randomUUID())
  const [receipt, setReceipt] = useState<GamePassGrant | null>(null)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [attempt, setAttempt] = useState<{
    requestId: string
    expectedUserId: string
    email: string
    count: number
    reason: string
  } | null>(null)
  const submitting = useRef(false)

  useEffect(() => {
    if (recipient.isFetchedAfterMount && recipient.data && !expectedUserId)
      setExpectedUserId(recipient.data.userId)
  }, [expectedUserId, recipient.data, recipient.isFetchedAfterMount])

  const changedRecipient = !!expectedUserId && !!recipient.data && expectedUserId !== recipient.data.userId
  const resolved = recipient.isFetchedAfterMount && !recipient.isError && !changedRecipient && recipient.data?.userId === expectedUserId ? recipient.data : null
  const history = useInfiniteQuery(trpc.promoGamePassRouter.adminListGrants.infiniteQueryOptions(
    { recipientId: expectedUserId ?? '', limit: 5 },
    { enabled: !!resolved, getNextPageParam: lastPage => lastPage.nextCursor ?? undefined },
  ))
  const grant = useMutation({
    ...trpc.promoGamePassRouter.adminGrant.mutationOptions(),
    retry: false,
    onSuccess: (result) => {
      submitting.current = false
      setReceipt(result)
      setAttempt(null)
      setErrorKey(null)
      void recipient.refetch()
      void history.refetch()
    },
    onError: (error) => {
      submitting.current = false
      const known = GRANT_ERRORS.has(error.message)
      setErrorKey(known ? `errors.${error.message}` : 'saveError')
      // An uncertain response must retry the exact same request and terms.
      if (known || error.data?.code === 'BAD_REQUEST')
        setAttempt(null)
      if (error.message.startsWith('game_pass_recipient_'))
        void recipient.refetch()
    },
  })
  const locked = grant.isPending || attempt !== null
  useEffect(() => {
    onLockedChange(locked)
    return () => onLockedChange(false)
  }, [locked, onLockedChange])
  useLayoutEffect(() => {
    document.getElementById(receipt ? 'game-pass-grant-again' : 'game-pass-grant-reason')?.focus()
  }, [receipt])

  const numericCount = Number(count)
  const validCount = !!count.trim() && Number.isSafeInteger(numericCount) && numericCount > 0 && numericCount <= MAX_BALANCE - (resolved?.remaining ?? 0)
  const validReason = reason.trim().length > 0 && reason.trim().length <= 200
  const rows = history.data?.pages.flatMap(page => page.grants)
  const recipientError = changedRecipient ? 'game_pass_recipient_changed' : recipient.error?.message
  const recipientErrorKey = recipientError && GRANT_ERRORS.has(recipientError) ? `errors.${recipientError}` : 'lookupError'

  function changed() {
    setErrorKey(null)
    setRequestId(crypto.randomUUID())
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting.current || receipt)
      return
    if (attempt) {
      submitting.current = true
      setErrorKey(null)
      grant.mutate(attempt)
      return
    }
    setCountTouched(true)
    setReasonTouched(true)
    if (!resolved || recipient.isFetching || !validCount || !validReason)
      return
    const nextAttempt = { requestId, expectedUserId: resolved.userId, email: resolved.email, count: numericCount, reason: reason.trim() }
    submitting.current = true
    setAttempt(nextAttempt)
    setErrorKey(null)
    grant.mutate(nextAttempt)
  }

  function grantAgain() {
    setReceipt(null)
    setReason('')
    setReasonTouched(false)
    setCountTouched(false)
    setRequestId(crypto.randomUUID())
    setErrorKey(null)
  }

  return (
    <div>
      {(!recipient.isFetchedAfterMount || recipient.isFetching) && !recipient.data && <p role="status" className="mt-3 text-sm text-muted-foreground">{t('promoCodes.lookingUp')}</p>}
      {(recipient.isError || changedRecipient) && (
        <div className="mt-3 space-y-2">
          <p data-testid="game-pass-recipient-error" role="alert" className="text-sm text-destructive">{t(`promoCodes.${recipientErrorKey}`)}</p>
          {!changedRecipient && <Button type="button" variant="outline" size="sm" disabled={recipient.isFetching} onClick={() => recipient.refetch()}>{t('promoCodes.retry')}</Button>}
        </div>
      )}
      {resolved && (
        <div data-testid="game-pass-recipient" className="mt-4 flex flex-wrap items-center justify-between gap-4 border-b pb-5 pt-3">
          <div className="min-w-0 space-y-1">
            <p className="break-words text-sm font-semibold">{resolved.name || resolved.email}</p>
            <p className="break-all text-xs text-muted-foreground" dir="ltr">{resolved.email}</p>
          </div>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {t('promoCodes.currentlyAvailable')}{' '}
            <strong data-testid="game-pass-current-balance" className="mx-1 text-xl font-semibold tabular-nums text-foreground">{resolved.remaining}</strong>{' '}
            {t('promoCodes.passesUnit')}
          </p>
        </div>
      )}
      {receipt ? (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-5">
          <p data-testid="game-pass-grant-result" role="status" className="min-w-0 break-words text-sm font-medium [overflow-wrap:anywhere]">{t('promoCodes.receipt').replace('{name}', receipt.name || receipt.email).replace('{count}', String(receipt.count))}</p>
          <Button id="game-pass-grant-again" data-testid="game-pass-grant-again" type="button" variant="outline" onClick={grantAgain} className="min-h-11">{t('promoCodes.grantAgain')}</Button>
        </div>
      ) : (resolved || attempt) && (
        <form data-testid="game-pass-grant-form" onSubmit={submit} className="mt-5 space-y-5" aria-busy={grant.isPending}>
          <div className="grid items-start gap-4 sm:grid-cols-[146px_minmax(0,1fr)] sm:gap-5">
            <div className="space-y-2">
              <Label htmlFor="game-pass-grant-count">{t('promoCodes.count')}</Label>
              <Input id="game-pass-grant-count" data-testid="game-pass-grant-count" type="number" min={1} max={MAX_BALANCE - (resolved?.remaining ?? 0)} step={1} inputMode="numeric" value={count} onChange={(event) => { changed(); setCount(event.target.value); setCountTouched(true) }} onBlur={() => setCountTouched(true)} required disabled={locked} aria-invalid={countTouched && !validCount} aria-describedby={countTouched && !validCount ? 'game-pass-count-error' : undefined} className="min-h-11 tabular-nums" />
              {countTouched && !validCount && <p id="game-pass-count-error" role="alert" className="text-sm text-destructive">{t('promoCodes.countError')}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="game-pass-grant-reason">{t('promoCodes.reason')}</Label>
              <Input id="game-pass-grant-reason" data-testid="game-pass-grant-reason" value={reason} onChange={(event) => { changed(); setReason(event.target.value) }} onBlur={() => setReasonTouched(true)} required maxLength={200} disabled={locked} placeholder={t('promoCodes.reasonPlaceholder')} aria-invalid={reasonTouched && !validReason} aria-describedby={reasonTouched && !validReason ? 'game-pass-reason-error' : undefined} className="min-h-11" />
              {reasonTouched && !validReason && <p id="game-pass-reason-error" role="alert" className="text-sm text-destructive">{t('promoCodes.reasonError')}</p>}
            </div>
          </div>
          {errorKey && <p data-testid="game-pass-grant-error" role="alert" className="text-sm text-destructive">{t(`promoCodes.${errorKey}`)}</p>}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p data-testid="game-pass-grant-estimate" className="text-sm text-muted-foreground" aria-live="polite">{resolved && validCount ? t('promoCodes.estimate').replace('{count}', String(resolved.remaining + numericCount)) : ''}</p>
            <Button data-testid="game-pass-grant-submit" type="submit" disabled={grant.isPending || (!attempt && (!resolved || recipient.isFetching || !validCount || !validReason))} className="min-h-11">{t(grant.isPending ? 'promoCodes.granting' : attempt ? 'promoCodes.retryGrant' : 'promoCodes.add').replace('{count}', String(numericCount))}</Button>
          </div>
        </form>
      )}
      {resolved && (
        <section data-testid="game-pass-grant-history" className="mt-7 border-t pt-6" aria-labelledby="game-pass-grant-history-heading">
          <div className="mb-4 flex items-baseline justify-between gap-3">
            <h3 id="game-pass-grant-history-heading" className="text-sm font-semibold">{t('promoCodes.history')}</h3>
            <span className="text-xs text-muted-foreground">{t('promoCodes.selectedUser')}</span>
          </div>
          {history.isError && (
            <div className="space-y-2">
              <p role="alert" className="text-sm text-destructive">{t('promoCodes.loadError')}</p>
              <Button type="button" variant="outline" size="sm" onClick={() => history.refetch()}>{t('promoCodes.retry')}</Button>
            </div>
          )}
          {!history.data && !history.isError && <p role="status" className="text-sm text-muted-foreground">{t('promoCodes.loading')}</p>}
          {rows?.length === 0 && <p className="py-3 text-sm text-muted-foreground">{t('promoCodes.empty')}</p>}
          {!!rows?.length && (
            <table className="block w-full text-sm sm:table sm:table-fixed">
              <caption className="sr-only">{t('promoCodes.historyCaption')}</caption>
              <thead className="hidden sm:table-header-group">
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th scope="col" className="w-[23%] pb-3 pr-3 font-medium">{t('promoCodes.createdAt')}</th>
                  <th scope="col" className="w-[15%] pb-3 pr-3 font-medium">{t('promoCodes.added')}</th>
                  <th scope="col" className="w-[20%] pb-3 pr-3 font-medium">{t('promoCodes.historicalBalance')}</th>
                  <th scope="col" className="w-[25%] pb-3 pr-3 font-medium">{t('promoCodes.historyReason')}</th>
                  <th scope="col" className="pb-3 font-medium">{t('promoCodes.operator')}</th>
                </tr>
              </thead>
              <tbody className="block sm:table-row-group">
                {rows.map(row => (
                  <tr key={row.id} data-testid={`game-pass-grant-row-${row.id}`} className="relative block border-b py-3 last:border-0 sm:table-row sm:py-0">
                    <td className="flex gap-2 py-1 pr-14 tabular-nums sm:table-cell sm:py-4 sm:pr-3 sm:align-top"><span className="w-24 shrink-0 text-muted-foreground sm:hidden">{t('promoCodes.createdAt')}</span><time dateTime={new Date(row.createdAt).toISOString()}>{new Date(row.createdAt).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US')}</time></td>
                    <td className="absolute right-0 top-4 font-semibold tabular-nums sm:static sm:table-cell sm:py-4 sm:pr-3 sm:align-top"><span className="sr-only">{t('promoCodes.added')}: </span>+{row.count}</td>
                    <td className="flex gap-2 py-1 tabular-nums sm:table-cell sm:py-4 sm:pr-3 sm:align-top"><span className="w-24 shrink-0 text-muted-foreground sm:hidden">{t('promoCodes.historicalBalance')}</span>{row.balanceBefore} → {row.balanceAfter}</td>
                    <td className="flex gap-2 py-1 sm:table-cell sm:py-4 sm:pr-3 sm:align-top"><span className="w-24 shrink-0 text-muted-foreground sm:hidden">{t('promoCodes.historyReason')}</span><span className="min-w-0 break-words [overflow-wrap:anywhere]">{row.reason}</span></td>
                    <td className="flex gap-2 py-1 sm:table-cell sm:py-4 sm:align-top"><span className="w-24 shrink-0 text-muted-foreground sm:hidden">{t('promoCodes.operator')}</span><span className="min-w-0 break-words [overflow-wrap:anywhere]">{row.operator.name || row.operator.email}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {history.hasNextPage && <Button data-testid="game-pass-grants-load-more" type="button" variant="outline" disabled={history.isFetchingNextPage} onClick={() => history.fetchNextPage()} className="mt-4 min-h-11">{t(history.isFetchingNextPage ? 'promoCodes.loadingMore' : 'promoCodes.loadMore')}</Button>}
        </section>
      )}
    </div>
  )
}
